"use client";

import React, { FC, useEffect, useRef, useState } from "react";
import { CircleHelp, CirclePlay } from "lucide-react";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { DataRows } from "../utils/table-helpers";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./accordion";

// Shared evaluation "chrome" — the intro banner and the pre-calculate
// empty-state card that surround the shared <EvaluationResults> graphics.
// Both the borrower simulation wizard (onboard-widget.tsx) and the lender
// referral wizard (partner-referral-wizard.tsx) render these so their
// evaluation steps stay visually aligned.

// How-to walkthrough source for the Capital Simulation Tool. Source-agnostic
// by design: the panel renders an <iframe> for embed hosts (Vimeo, YouTube,
// Cloudflare Stream…) or a native <video> for a self-hosted file. When the
// final hosting decision is made, change ONLY the CST_HOW_TO_VIDEO object
// below — the render logic in CstHowToVideo adapts automatically.
//
// Swap examples:
//   YouTube:   { kind: "embed", src: "https://www.youtube-nocookie.com/embed/<id>", title: "…" }
//   Self-host: { kind: "file",  src: "https://<cdn>/how-to-cst.mp4", title: "…", poster: "https://<cdn>/how-to-cst.jpg" }
//
// CURRENT source: self-hosted MP4 in the public widget's S3 bucket
// (cxm-widget, served at cxm-widget.s3.amazonaws.com). Plays on every domain —
// including the embeddable widget on third-party sites — with no Vimeo-style
// domain whitelist. Stored under assets/ (outside the build's `static/` prefix)
// so a widget rebuild that syncs `static/` can't clobber it. To go back to a
// Vimeo embed, switch to { kind: "embed", src:
// "https://player.vimeo.com/video/<id>?…" } (note: Vimeo domain-restricts).
type CstHowToVideoSource =
  | { kind: "embed"; src: string; title: string }
  | { kind: "file"; src: string; title: string; poster?: string };

const CST_HOW_TO_VIDEO: CstHowToVideoSource = {
  kind: "file",
  src: "https://cxm-widget.s3.amazonaws.com/assets/how-to-cst.mp4",
  title: "How to use the Capital Simulation Tool",
};

// Renders the configured walkthrough source: an <iframe> for embed hosts
// (Vimeo/YouTube/…) or a native <video> for a self-hosted file. Takes the
// source as a typed parameter — rather than reading the narrowed module
// const inline — so BOTH branches stay type-checked. (A const is narrowed
// to its initializer's variant, which would make the unused branch dead
// code; a union-typed parameter keeps both reachable.)
// `videoRef`/`onEnded` are wired only on the self-hosted <video> branch:
// the parent uses the ref to start playback after a delay and `onEnded`
// to auto-collapse the panel. An embed <iframe> can do neither across
// origins, so both are simply ignored when source.kind === "embed".
const HowToPlayer: FC<{
  source: CstHowToVideoSource;
  videoRef?: React.RefObject<HTMLVideoElement>;
  onEnded?: () => void;
}> = ({ source, videoRef, onEnded }) =>
  source.kind === "embed" ? (
    <iframe
      src={source.src}
      title={source.title}
      className="absolute inset-0 h-full w-full rounded-md"
      allow="fullscreen; picture-in-picture; clipboard-write"
      allowFullScreen
    />
  ) : (
    <video
      ref={videoRef}
      src={source.src}
      poster={source.poster}
      title={source.title}
      className="absolute inset-0 h-full w-full rounded-md"
      controls
      preload="metadata"
      playsInline
      onEnded={onEnded}
    />
  );

// Collapsible "How to use the CST" walkthrough, styled as the primary
// "To Get Started…" banner shown above the intro helper box. Collapsed by
// default; users click to expand and watch the walkthrough via the shared
// Radix Accordion. Rendered on every CST surface via
// <EvaluationIntroBanner /> below.
export const CstHowToVideo: FC = () => {
  // Controlled accordion value: "cst-how-to" when the panel is open, ""
  // when collapsed. We drive it (instead of letting Radix self-manage)
  // so the panel can auto-collapse itself when the walkthrough ends.
  const [openValue, setOpenValue] = useState<string>("");
  const videoRef = useRef<HTMLVideoElement>(null);

  // When the panel opens, start playback after a 1s beat so the expand
  // animation settles first. Re-arms on every open; the cleanup cancels
  // the pending timer if the user collapses within that second. Only the
  // self-hosted <video> branch is driven — videoRef is null for an embed
  // <iframe>, so the timer is a harmless no-op in that case.
  useEffect(() => {
    if (openValue !== "cst-how-to") return;
    const timer = setTimeout(() => {
      const video = videoRef.current;
      if (!video) return;
      // Reopened after a prior completion → restart from the top.
      if (video.ended) video.currentTime = 0;
      // Opening the panel is a user gesture, so unmuted playback is
      // permitted within the browser's activation window; if a stricter
      // policy still blocks it, the visible controls remain usable.
      void video.play().catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [openValue]);

  return (
    <Accordion
      type="single"
      collapsible
      className="mb-2"
      value={openValue}
      onValueChange={setOpenValue}
    >
      <AccordionItem
        value="cst-how-to"
        className="overflow-hidden rounded-md bg-white shadow-sm"
      >
        <AccordionTrigger className="bg-primary px-4 py-3 text-lg font-semibold leading-7 text-primary-foreground no-underline transition hover:no-underline hover:brightness-95 [&>svg]:text-primary-foreground">
          {/* flex-1 centers the label left of the chevron; ml-4 (the
              chevron's width) shifts it back to the bar's true center. */}
          <span className="ml-4 flex flex-1 items-center justify-center gap-3">
            <CirclePlay width={24} height={24} aria-hidden />
            To Get Started - Click Here to Watch a Quick Overview Video
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-4">
          <div className="mx-auto w-full max-w-2xl">
            {/* 16:9 responsive wrapper — pb-[56.25%] holds the aspect ratio
                while the player (iframe or <video>) fills it absolutely. */}
            <div className="relative w-full pb-[56.25%]">
              <HowToPlayer
                source={CST_HOW_TO_VIDEO}
                videoRef={videoRef}
                onEnded={() => setOpenValue("")}
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

// White intro helper box: the "complete the sections… then click See
// Funding Options" guidance on the left with an inline "Quick Tips"
// Popover trigger on the right.
const IntroBannerBar: FC = () => (
  <div className="mb-2 flex items-center justify-between gap-4 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
    <p className="text-sm font-medium text-gray-700">
      Complete the sections on the left below then click "See Funding
      Options" to get your anonymous fundability assessment, funding options,
      and estimated terms
    </p>
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-[30px] shrink-0 shadow-sm"
        >
          <CircleHelp width={16} height={16} className="mr-2" />Quick Tips
        </Button>
      </PopoverTrigger>
      <PopoverContent className="md:min-w-[600px] max-h-[600px] overflow-y-auto flex-col flex space-y-3">
        <b>HOW TO USE THE CAPITAL SIMULATION TOOL</b>
        Anonymous - No Registration Required
        <div className="text-lg">
          <b>Getting Started</b>
        </div>
        <p>
          Fill out the form on the left side of your screen with your
          business information.
        </p>
        <div className="border-b border-black w-full"></div>
        <b>
          About Your Company
        </b>
        <ul className="list-disc pl-6">
          <li>
            Zip Code – your company location (helps match local funding
            options)
          </li>
          <li>
            Company Stage – Select from dropdown
          </li>
          <li>
            Industry – Choose the closest match to your business
          </li>
        </ul>
        <b>
          Funds needed
        </b>
        <ul className="list-disc pl-6">
          <li>
            Amount – How much funding you're seeking
          </li>
          <li>
            Usage – Select primary use (multiple uses OK, pick the main
            one)
          </li>
          <li>
            Timeline – When you need the funds
          </li>
        </ul>
        <b>Company Metrics</b>
        <p>
          <i>All numbers can be estimates – exact figures not required</i>
        </p>
        <b>Revenue & Profitability:</b>
        <ol className="list-decimal pl-6">
          <li>Previous 12 Months Revenue – Your total gross revenue</li>
          <li>
            Previous 12 Months Operating Profit – Profits before financing
            and accounting costs (EBITDA)
          </li>
          <li>
            Previous 12 Months Net Income – Final profit after all
            expenses
          </li>
        </ol>
        <b>Assets & Collateral:</b>
        <ol className="list-decimal pl-6">
          <li>
            Inventory Value – Total inventory worth (indicate any used as
            collateral)
          </li>
          <li>
            Real Estate Value – Property values (indicate any used as
            collateral)
          </li>
          <li>
            Equipment Value – Vehicles, machinery, tools, etc. (indicate
            any used as collateral)
          </li>
        </ol>
        <b>Debt & Cash Flow:</b>
        <ol className="list-decimal pl-6">
          <li>Total Debt – All outstanding company debts</li>
          <li>Monthly Debt Service – Total monthly loan payments</li>
          <li>
            Average Monthly Accounts Receivable – 12-month average of
            money owed by customers
          </li>
          <li>
            Average Monthly Purchase Orders – 12-month average value of
            customer orders
          </li>
        </ol>
        <div className="w-full border-b border-black"></div>
        <b className="text-lg">View Your Results</b>
        <p>
          Click "See Funding Options" to generate your personalized
          results.
        </p>
        <b>Understanding Your Results:</b>
        <b>The Funding Wheel (right side)</b>
        <ul className="list-disc pl-6">
          <li>Hover over any slice to explore different capital types</li>
          <li>
            The highlighted slice shows your optimal funding match based
            on monthly payment
          </li>
        </ul>
        <b>Fundability Score</b>
        <ul className="list-disc pl-6">
          <li>
            Click "Understand Your Score" to see how lenders evaluate your
            business strengths and weaknesses
          </li>
        </ul>
        <b>Funding Options Table</b>
        <ul className="list-disc pl-6">
          <li>View all available options</li>
          <li>
            Sort by any column (amount, payment, rates, terms, timeline,
            interested lenders)
          </li>
          <li>Click any option for detailed information</li>
        </ul>
        <b>Next Steps:</b>
        <ul className="list-disc pl-6">
          <li>
            Adjust any numbers on the left to see how changes affect your
            options
          </li>
          <li>
            When ready, click the button at bottom right to access
            advanced tools and connect with lenders
          </li>
        </ul>
        <div className="border-b border-black w-full"></div>
        <i>
          Tip: You can update any information on the left side at any time
          to explore different scenarios
        </i>
      </PopoverContent>
    </Popover>
  </div>
);

// Stacks the "To Get Started" video banner above the white intro helper
// box. Both onboard-widget.tsx (borrower) and partner-referral-wizard.tsx
// (lender) render this, so the walkthrough appears on every CST surface.
// Wrapped in a single <div> so it stays one root element for the flex
// layouts that host it.
export const EvaluationIntroBanner: FC = () => (
  <div>
    <CstHowToVideo />
    <IntroBannerBar />
  </div>
);

// Blue "Indicative Loan Options and Terms" card — the pre-calculate
// empty state shown before the user runs an evaluation. Renders a
// DataRows table of "---" placeholders.
export const IndicativeLoanOptionsCard: FC = () => (
  <div className="bg-primary text-white rounded-lg shadow-md p-4 border border-gray-300">
    <h2 className="text-xl font-semibold mb-2">
      Indicative Loan Options and Terms
    </h2>
    <DataRows
      className="mt-4 text-xl text-white"
      data={{
        fundingOptions: "---",
        loanType: "---",
        interestRate: "---",
        monthlyPayment: "---",
        maturityInMonths: "---",
        potentialFundsAvailable: "---",
        matchedLenders: "---",
        daysToFund: "---",
      }}
      fields={{
        maturityInMonths: {
          label: "Estimated Term",
        },
        interestRate: {
          label: "Estimated Rate",
        },
        monthlyPayment: {
          label: "Estimated Monthly Payment",
        },
        potentialFundsAvailable: {
          label: "Max Potential",
        },
        daysToFund: {
          label: "Timeline",
        },
      }}
    />
  </div>
);
